import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";
import { orientationScheduleTableColumns } from "../../../data/FormColumns/Recruiting/orientationScheduleTableColumns";
import { OrientationDto } from "../../../interfaces/Recruiting/orientation";

const fetchOrientations = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/recruiting/orientations`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const OrientationScheduleTable = () => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["orientations"],
    queryFn: () => fetchOrientations(token),
    enabled: !!token,
  });

  const filteredOrientations = useMemo(() => {
    if (!data) return [];
    return (data as OrientationDto[]).filter((orientation) =>
      [
        orientation.driverFirstName,
        orientation.driverLastName,
        orientation.company,
        orientation.status,
        orientation.recruiter,
        orientation.division,
        orientation.email,
        orientation.note,
      ].some((field) =>
        field?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [data, searchTerm]);

  return (
    <Box className="incidents-table-container" height={900}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by driver, company, status, recruiter, division, email, or note"
          sx={{ maxWidth: "100%", width: 400 }}
        />
      </Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Error: {(error as any).message}</Typography>
      ) : (
        <PersistedDataGrid
          tableName="orientationSchedule"
          rows={filteredOrientations}
          columns={orientationScheduleTableColumns}
          compactViewConfig={{
            primaryField: "driverName",
            secondaryFields: ["orientationDate", "company"],
            detailFields: [
              "referral",
              "driverHireType",
              "recruiter",
              "division",
              "state",
              "primaryPhoneNumber",
              "email",
              "trailerType",
              "truckUnitNumber",
              "trailerUnitNumber",
              "pay",
            ],
            actionFields: ["update"],
            statusField: "status",
          }}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default OrientationScheduleTable;
